import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  Input,
  Text,
  Box,
  Flex,
  VStack,
  HStack,
  Icon,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast,
} from "@chakra-ui/react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { createConsultantFromCv } from "../../../api/consultantsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import ConsultantForm from "./ConsultantForm";
import { FieldLabel } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

// Token step indicator: numbered dots + labels with a navy "active" state.
const StepIndicator = ({ step, labels }) => (
  <HStack spacing="8px" mb="16px" align="center">
    {labels.map((label, idx) => {
      const n = idx + 1;
      const isActive = n === step;
      const isDone = n < step;
      return (
        <React.Fragment key={n}>
          <HStack spacing="8px" align="center">
            <Flex
              align="center"
              justify="center"
              boxSize="22px"
              borderRadius="999px"
              fontSize="11px"
              fontWeight="800"
              bg={isActive || isDone ? "portal.primary" : "portal.soft"}
              color={isActive || isDone ? "white" : "portal.muted"}
              borderWidth="1px"
              borderColor={isActive || isDone ? "portal.primary" : "portal.line"}
              flexShrink={0}
            >
              {n}
            </Flex>
            <Text
              fontSize="12px"
              fontWeight={isActive ? "800" : "600"}
              color={isActive ? "portal.text" : "portal.muted"}
              sx={{ marginBottom: "0 !important" }}
            >
              {label}
            </Text>
          </HStack>
          {n < labels.length && (
            <Box flex="1" h="1px" bg="portal.line" minW="16px" />
          )}
        </React.Fragment>
      );
    })}
  </HStack>
);

const CreateConsultantWizardModal = ({ isOpen, onClose, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isSupportedFile = (f) => {
    if (!f) return false;
    const mime = f.type;
    const name = (f.name || "").toLowerCase();
    if (
      mime === "application/pdf" ||
      mime === "application/msword" ||
      mime ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return true;
    }
    if (
      name.endsWith(".pdf") ||
      name.endsWith(".doc") ||
      name.endsWith(".docx")
    ) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setFile(null);
    setExtractedData(null);
    setDragActive(false);
  }, [isOpen]);

  const handleFilesSelected = (event) => {
    const selected = event.target.files?.[0] || null;
    if (selected && isSupportedFile(selected)) {
      setFile(selected);
    } else if (selected) {
      toast({
        title: t("pilotNeeds.toasts.invalidFileTypeTitle"),
        description: t("pilotNeeds.toasts.invalidFileTypeDescriptionFile"),
        status: "error"
      });
    }
    event.target.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const f = e.dataTransfer.files[0];
      if (isSupportedFile(f)) {
        setFile(f);
      } else {
        toast({
          title: t("pilotNeeds.toasts.invalidFileTypeTitle"),
          description: t("pilotNeeds.toasts.invalidFileTypeDescriptionFile"),
          status: "error"
        });
      }
    }
  };

  const handleContinueWithUpload = async () => {
    if (!file) return;
    setIsSubmitting(true);
    try {
      const created = await createConsultantFromCv(file, undefined, {
        buId: selectedBusinessUnitId || undefined
      });
      toast({
        title: t("pilotConsultants.importModal.toasts.importCompleted"),
        status: "success"
      });
      setExtractedData(created || null);
      setStep(2);
    } catch (e) {
      toast({
        title: t("pilotConsultants.importModal.toasts.importFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotConsultants.importModal.toasts.importFailedDescription"),
        status: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueWithoutUpload = () => {
    setExtractedData(null);
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setExtractedData(null);
    setFile(null);
  };

  const handleFormSuccess = (created) => {
    onSuccess?.(created);
    onClose?.();
  };

  const handleClose = () => {
    setStep(1);
    setFile(null);
    setExtractedData(null);
    onClose?.();
  };

  const hasFile = Boolean(file);

  const handleContinue = () => {
    if (hasFile) {
      handleContinueWithUpload();
    } else {
      handleContinueWithoutUpload();
    }
  };

  const stepLabels = [
    t("pilotConsultants.createWizard.stepLabels.upload", { defaultValue: "Upload CV" }),
    t("pilotConsultants.createWizard.stepLabels.review", { defaultValue: "Review details" })
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={step === 2 ? "5xl" : "lg"}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {step === 1
            ? t("pilotConsultants.createWizard.step1Title")
            : t("pilotConsultants.createWizard.step2Title")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody bg="portal.bg" py="16px">
          <StepIndicator step={step} labels={stepLabels} />
          {step === 1 ? (
            <>
              <Text fontSize="13px" color="portal.muted" mb="14px" sx={{ marginBottom: "14px !important" }}>
                {t("pilotConsultants.createWizard.step1Description")}
              </Text>
              <FormControl mb="16px">
                <FieldLabel sx={{ marginBottom: "6px !important" }}>{t("pilotConsultants.importModal.fields.file")}</FieldLabel>
                {file ? (
                  <Tag size="md" variant="subtle" colorScheme="blue" borderRadius="999px">
                    <TagLabel>{file.name}</TagLabel>
                    <TagCloseButton onClick={() => setFile(null)} />
                  </Tag>
                ) : (
                  <Box
                    border="2px dashed"
                    borderColor={dragActive ? "portal.primary" : "portal.line"}
                    borderRadius="10px"
                    p="20px"
                    textAlign="center"
                    minH="160px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bg={dragActive ? "portal.active" : "portal.soft"}
                    transition="all 0.2s"
                    cursor="pointer"
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    _hover={{ bg: "portal.active", borderColor: "portal.primary" }}
                  >
                    <VStack spacing="8px">
                      <Icon
                        as={FaCloudUploadAlt}
                        boxSize="28px"
                        color="portal.primary"
                      />
                      <Text fontSize="13px" fontWeight="700" color="portal.text" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotConsultants.importModal.dropzone.text")}
                      </Text>
                      <Text fontSize="11px" color="portal.muted" sx={{ marginBottom: "0 !important" }}>
                        {t("pilotConsultants.importModal.dropzone.accepted")}
                      </Text>
                    </VStack>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleFilesSelected}
                      display="none"
                    />
                  </Box>
                )}
              </FormControl>
            </>
          ) : (
            <ConsultantForm
              initialData={extractedData}
              onSuccess={handleFormSuccess}
              onCancel={handleClose}
              isEdit={Boolean(extractedData?.id)}
              showBackButton={true}
              onBack={handleBack}
            />
          )}
        </ModalBody>
        {step === 1 && (
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={handleClose}>
              {t("pilotConsultants.importModal.buttons.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleContinue}
              isLoading={hasFile && isSubmitting}
              minW="200px"
              px="16px"
            >
              {hasFile
                ? t("pilotConsultants.createWizard.buttons.continueWithUpload")
                : t(
                    "pilotConsultants.createWizard.buttons.continueWithoutUpload"
                  )}
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default CreateConsultantWizardModal;
